<script lang="ts" setup>
import SiteconnectHeader from '@/components/siteconnect/header/SiteconnectHeader.vue'
import ProductOfferCard from '@/components/siteconnect/offer/ProductOfferCard.vue'
import EntryForm from '@/components/organisms/forms/entry/EntryForm.vue'
import { computed } from 'vue'
import { translate } from '@/i18n/i18n.ts'
import { useGlobalErrorService } from '@/services/error.service.ts'
import { useAppFormService } from '@/libs/api/appform/app-form.service.ts'
import type { ProductOfferResponse } from '@/libs/api/insurance/insurance.types.ts'
import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import SiteconnectAdditionalContentIntro from '@/components/siteconnect/intro/SiteconnectAdditionalContentIntro.vue'
import SiteconnectAdditionalContentOutro from '@/components/siteconnect/outro/SiteconnectAdditionalContentOutro.vue'
import LinexoScoping from '@/components/linexo-scoping/LinexoScoping.vue'
import { useRedirectButtonSetup } from '@/components/atom/redirect/redirect-button-setup.composable'
import { useDefaultVariantForm } from './default-view-state.composable'
import { LButton } from '@wgg.hyve/hyve-ui-library'

const state = useDefaultVariantForm(useGlobalErrorService())
const {
  validationResult,
  handleShowOffers,
  handleOnFormUpdate,
  handleFormDataUpdate,
  handleValidationResult,
  latestTariffState: tariffState,
  formState,
  currentOffers,
  currentProductSpecs
} = state

const appFormService = useAppFormService()
const connectStore = useConnectStore()

useRedirectButtonSetup()

// Combines product offers and associated product specification
const productCardData = computed(() => {
  if (!validationResult.value?.isValid) {
    return []
  }

  const offers = currentOffers.value
  const productSpecifications = currentProductSpecs.value
  const result = [
    ...offers.flatMap((offer) => {
      const offerProductSpecification = productSpecifications[offer.id]
      return offerProductSpecification
        ? [{ offer, specs: offerProductSpecification, deviceClassId: offer.deviceClassId }]
        : []
    })
  ]
  return result
})

const partnerContent = computed(() => connectStore.getPartnerContent)
const additionalContentIntro = computed(() => partnerContent.value?.intro)
const additionalContentOutro = computed(() => partnerContent.value?.outro)

function getTargetUrl(offer: Pick<ProductOfferResponse, 'id'>, offerDeviceClassId: string): string {
  const checkoutUrl = translate('checkout.target_url_bike')

  return appFormService.buildPrefillUrl(checkoutUrl, {
    device_class_id: offerDeviceClassId,
    product_id: offer?.id,
    device_purchase_price: tariffState.value?.purchasePrice,
    device_purchase_date: tariffState.value?.purchaseDate,
    partner: connectStore.dataAttributes?.brokerNumber,
    sc_sid: connectStore.dataAttributes?.partnerId,
    prefill: connectStore.dataAttributes?.prefill
  })
}
</script>

<template>
  <LinexoScoping
    class="linexo-siteconnect-default"
    :cta-variant="connectStore.getLinexoSettings?.ctaVariant"
    :boxed-mode="connectStore.theme?.boxedMode"
  >
    <template v-slot:intro>
      <SiteconnectAdditionalContentIntro
        v-if="additionalContentIntro"
        :headline="additionalContentIntro.headline"
        :subheadline="additionalContentIntro.subheadline"
        :benefits="additionalContentIntro.benefits"
      />
    </template>
    <template v-slot:default>
      <div class="linexo-siteconnect-default__form">
        <SiteconnectHeader />

        <EntryForm
          ref="entryForm"
          v-model:form-data="formState"
          v-model:validation-result="validationResult"
          variant="default"
          @update:form-data="handleFormDataUpdate"
          @update:validation-result="handleValidationResult"
          @on-update="handleOnFormUpdate"
        >
          <template #footer>
            <LButton
              variant="primary"
              size="large"
              :disabled="!validationResult.isValid"
              @click="handleShowOffers"
            >
              {{ translate('content.button_text_tariff_calc') }}
            </LButton>
          </template>
        </EntryForm>

        <div class="product-offers" v-if="productCardData.length">
          <h3 class="product-offers__headline">
            {{ translate('content.product_offers_headline') }}
          </h3>
          <template v-if="productCardData.length > 0">
            <ProductOfferCard
              v-for="{ offer, specs, deviceClassId } in productCardData"
              :key="offer!.id"
              :offer="offer!"
              :specs="specs!"
              :target-url="getTargetUrl(offer!, deviceClassId)"
              :redirect-behavior="connectStore.getLinexoSettings!.redirectBehavior"
            />
          </template>
        </div>
      </div>
    </template>
    <template v-slot:outro>
      <SiteconnectAdditionalContentOutro
        v-if="additionalContentOutro"
        :text="additionalContentOutro.text"
        :image="additionalContentOutro.image"
        :headline="additionalContentOutro.headline"
      />
    </template>
  </LinexoScoping>
</template>

<style lang="scss" scoped>
@import 'default-view';
</style>
